import { React, bind } from '../../Imports';
import { TextField } from 'MaterialUIComponents';
import { CloseIcon, ExpandMoreIcon } from 'MaterialUIIcons';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { Checkbox, Typography } from '../../MaterialUIComponents';
import { CutText } from '$Utilities/dataModelUtilities';
import CustomPopper from './CustomPopper';

interface IAutocompleteDropdownProps {
    name: string;
    items: any[];
    selectedValues: any[] | undefined;
    onSelectedItemChange: (value: any[]) => void;
    error?: boolean;
    width?: number;
    id?: string;
}

const styles = require('./VPAutocompleteMultiselect.scss') as {
    menuItem: string;
    optionsBackground: string;
    tagItem: string;
    preventOverflow: string;
    inputItem: string;
    inputImg: string;
    dropdownIcon: string;
    checkbox: string;
    clearIcon: string;
    root: string;
};

const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: (option: any) => option.label,
});

export default class VPAutocompleteMultiSelect extends React.PureComponent<IAutocompleteDropdownProps> {
    state = {
        selectedItems: [] as object[],
        inputValue: '' as string,
        isComponentFocused: false,
        allSelected: false,
    };

    @bind
    handleChange(values: any): void {
        const { onSelectedItemChange } = this.props;

        const filteredValues = values.map((item: any) => item.value);

        onSelectedItemChange(filteredValues);
    }

    @bind
    toggleAll(): any {
        // this checks if the current state of allSelected, set in the Autocomplete's onChange. Ensures that either all items are selected or none are.
        this.setState({ selectedItems: this.state.allSelected ? this.props.items : [] }, () => {
            this.handleChange(this.state.selectedItems);
        });
    }

    componentDidUpdate(prevProps: Readonly<IAutocompleteDropdownProps>): void {
        if (this.props.selectedValues !== prevProps.selectedValues) {
            if (!this.props.selectedValues || this.props.selectedValues.length === 0) {
                this.setState({ selectedItems: [] });
            }
        }
    }

    render(): JSX.Element {
        // somewhat janky - but for consistent styling and to make sure the event occurs within the state management system for the Autocomplete component, I have appended the "select all" into the options
        const multiSelectOptions = [{ label: 'Select/Unselect All', value: 'toggle' }, ...this.props.items];

        return (
            <Autocomplete
                multiple={true}
                disableCloseOnSelect
                disableClearable
                disableListWrap
                clearOnBlur={true}
                openOnFocus={true}
                classes={{
                    root: styles.root,
                    option: styles.optionsBackground,
                }}
                onOpen={() => this.setState({ isComponentFocused: true })} // I did this because the focus state for autocomplete is usable within certain props. in this case, renderTagProps does not have an onFocus true/false value in it's typing, so I am writing my own control for it here.
                onClose={() => this.setState({ isComponentFocused: false })}
                limitTags={2}
                PopperComponent={CustomPopper}
                id={this.props.name}
                options={multiSelectOptions}
                getOptionLabel={(option): string => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value} // MUI 4 requires a value equality assertion if you are updating multiple values in the field dynamically. This goes away when we update to MUI 5 at some point in the future.
                onChange={(event: any, newValue: any, reason): void => {
                    newValue.some((val: any) => val.value === 'toggle')
                        ? // this checks if the user has selected the Select All/Unselect All option, which has "toggle" as its value.
                          this.setState({ allSelected: !this.state.allSelected }, () => {
                              this.toggleAll(); //toggle is executed synchronously through the callback function so that the allSelected state has time to toggle before it is used in a condition.
                          })
                        : // the condition in allSelected ensures that if all of the available options are clicked manually, the "Select All" checkbox becomes selected - and vice versa, for when a user clicks "Select All" and then manually unclicks one of the options.
                          this.setState({ selectedItems: newValue, allSelected: this.props.items.length === newValue.length }, () =>
                              this.handleChange(newValue),
                          );
                }}
                style={{
                    width: this.props.width ? this.props.width : 200,
                }}
                inputValue={this.state.isComponentFocused ? this.state.inputValue : ''}
                onInputChange={(event, value, reason) => {
                    // this, combined with inputValue, separates the state of the value property of the input field and the value property of the autocomplete component (i.e. the state of what the user types vs. the state of the values already selected by the user)
                    if (reason !== 'reset') {
                        this.setState({
                            inputValue: value,
                        });
                    }
                }}
                value={this.state.selectedItems}
                className={styles.preventOverflow}
                clearIcon={<CloseIcon className={styles.clearIcon} />}
                popupIcon={<ExpandMoreIcon className={styles.dropdownIcon} />}
                renderTags={(value, getTagProps) => {
                    const renderTagValues = value
                        .map((option: any) => {
                            return ` ${option.label}`;
                        })
                        .join(',');

                    return this.state.isComponentFocused ? (
                        ''
                    ) : (
                        <Typography style={{ width: 600, textAlign: 'left' }} noWrap={true}>
                            {renderTagValues}
                        </Typography>
                    ); //This is done because the autocomplete parent elements are flex elements, and do not support overflow styles. This fits all of the tags into a typography box - so that it's technically one tag with a fixed width, allowing us use of the ellipsis overflow style.
                }}
                filterOptions={filterOptions}
                renderOption={(props, option, { selected }) => (
                    <li {...props}>
                        <div className={styles.menuItem}>
                            {option.value === 'toggle' ? (
                                <Checkbox
                                    style={{ marginRight: 8, padding: 0 }}
                                    className={styles.checkbox}
                                    checked={this.state.allSelected}
                                />
                            ) : (
                                <Checkbox style={{ marginRight: 8, padding: 0 }} className={styles.checkbox} checked={selected} />
                            )}
                            <span>{CutText(option.label, 50)}</span>
                            {option.icon && <img src={option.icon} />}
                        </div>
                    </li>
                )}
                renderInput={(params): JSX.Element => {
                    return (
                        <TextField
                            {...params}
                            label={this.props.name}
                            placeholder={this.state.isComponentFocused ? 'Search' : ''}
                            id={this.props.id}
                            key={this.props.id}
                            variant="standard"
                            InputProps={{
                                ...params.InputProps,
                                inputProps: {
                                    ...params.inputProps, //params must be passed down here as well. InputProps and inputProps are NOT the same thing. see https://stackoverflow.com/questions/69870930/inputprops-vs-inputprops-in-mui-textfield
                                    maxLength: 20,
                                },
                                className: '', //Without this empty class here when selecting an item when an icon is present causes the whole input to shift up a few pixels
                            }}
                        />
                    );
                }}
            />
        );
    }
}
